import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { SBLogo } from "../components/WebLayout";
import { CheckCircle2, ShieldCheck, MapPin, ArrowLeft, ArrowRight } from "lucide-react";

const MACHINE_TYPES = ["Snack", "Drink", "Combo", "Smart Cooler", "Coffee"];

export default function BusinessVerification() {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "John Smith", title: "Operations Manager", phone: "(815) 555-0142", email: "j.smith@meridianlogistics.com",
    authorized: false,
    people: "100–250", open247: true, openTime: "07:00", closeTime: "19:00", shifts: "3",
    current: "no", desired: ["Snack", "Drink"], bestContact: "Weekdays 9AM–3PM CT",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const toggleDesired = (v) =>
    setForm({ ...form, desired: form.desired.includes(v) ? form.desired.filter((x) => x !== v) : [...form.desired, v] });

  const step1Valid = form.name && form.email && form.phone && form.authorized;
  const canSubmit = form.consent;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#080B0D] flex items-center justify-center p-6">
        <div className="sb-card p-10 max-w-md text-center">
          <CheckCircle2 size={56} className="text-[#FFC107] mx-auto" />
          <div className="mt-5 sb-eyebrow">Received</div>
          <h1 className="text-[28px] font-black mt-3">THANK YOU.</h1>
          <p className="mt-4 text-[#A1A1AA]">SpotBounty will follow up shortly with next steps.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-2xl mx-auto px-6 py-5"><SBLogo size={18} /></div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-5">
        {/* Business context — visible above wizard on all steps */}
        <div className="sb-card p-6">
          <div className="sb-eyebrow flex items-center gap-2"><MapPin size={12} className="text-[#FFC107]" /> Verification · {token}</div>
          <h1 className="mt-3 text-[22px] md:text-[26px] font-black leading-tight">
            You've been referred as the business contact for <span className="text-[#FFC107]">Meridian Logistics</span> in Joliet, IL.
          </h1>
          <p className="mt-3 text-[13.5px] text-[#A1A1AA] leading-relaxed">
            SpotBounty is a marketplace that connects your business with independent vending operators.
            This short form confirms your interest — no obligation, no fees, no commitment.
          </p>
        </div>

        {/* Progress */}
        <div className="sb-card p-4">
          <div className="flex items-center justify-between">
            <div className="sb-eyebrow" data-testid="verify-step-indicator">Step {step} of 3</div>
            <div className="text-[11px] text-[#A1A1AA]">
              {step === 1 && "Confirm who you are"}
              {step === 2 && "Confirm the location"}
              {step === 3 && "Confirm interest"}
            </div>
          </div>
          <div className="mt-3 sb-progress-track">
            <div className="sb-progress-fill" style={{ width: `${(step / 3) * 100}%` }} />
          </div>
          <div className="mt-2 flex justify-between">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`w-1.5 h-1.5 rounded-full ${n <= step ? "bg-[#FFC107]" : "bg-[#2A2F34]"}`} />
            ))}
          </div>
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div data-testid="verify-step-1" className="sb-card p-6 space-y-4">
            <div className="sb-eyebrow">Confirm Who You Are</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="sb-label">Manager Name</label><input required data-testid="verify-name" className="sb-input" value={form.name} onChange={set("name")} /></div>
              <div><label className="sb-label">Title</label><input data-testid="verify-title" className="sb-input" value={form.title} onChange={set("title")} /></div>
              <div><label className="sb-label">Phone</label><input required data-testid="verify-phone" className="sb-input" value={form.phone} onChange={set("phone")} /></div>
              <div><label className="sb-label">Email</label><input required type="email" data-testid="verify-email" className="sb-input" value={form.email} onChange={set("email")} /></div>
            </div>
            <label className="flex items-start gap-3 text-[13px] pt-2">
              <input type="checkbox" data-testid="verify-authorized" className="mt-1 accent-[#FFC107]" checked={form.authorized} onChange={set("authorized")} />
              <span>I am authorized to discuss vending service for this location.</span>
            </label>
            <button
              disabled={!step1Valid}
              onClick={() => setStep(2)}
              data-testid="verify-next-1"
              className="sb-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div data-testid="verify-step-2" className="sb-card p-6 space-y-4">
            <div className="sb-eyebrow">Confirm The Location</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="sb-label">Approx People</label><input data-testid="verify-people" className="sb-input" value={form.people} onChange={set("people")} /></div>
              <div>
                <label className="sb-label">Hours</label>
                <label className="flex items-center gap-2 text-[13px]">
                  <input type="checkbox" checked={form.open247} onChange={set("open247")} /> Open 24/7
                </label>
                {!form.open247 && (
                  <div className="flex gap-2 mt-2">
                    <input type="time" className="sb-input" value={form.openTime} onChange={set("openTime")} />
                    <input type="time" className="sb-input" value={form.closeTime} onChange={set("closeTime")} />
                  </div>
                )}
              </div>
              <div>
                <label className="sb-label">Number of Shifts</label>
                <div className="flex gap-2">
                  {["1", "2", "3"].map((n) => (
                    <button key={n} type="button" onClick={() => setForm({ ...form, shifts: n })} className={`sb-pill ${form.shifts === n ? "sb-pill-gold" : "sb-pill-gray"}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="sb-label">Current Vending?</label>
                <div className="flex gap-2">
                  {[["yes", "Yes"], ["no", "No"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setForm({ ...form, current: k })} className={`sb-pill ${form.current === k ? "sb-pill-gold" : "sb-pill-gray"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="sb-label">Desired Vending Type</label>
                <div className="flex flex-wrap gap-2">
                  {MACHINE_TYPES.map((m) => (
                    <button key={m} type="button" onClick={() => toggleDesired(m)} className={`sb-pill ${form.desired.includes(m) ? "sb-pill-gold" : "sb-pill-gray"}`}>{m}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2"><label className="sb-label">Best Contact Time</label><input data-testid="verify-besttime" className="sb-input" value={form.bestContact} onChange={set("bestContact")} /></div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(1)} data-testid="verify-back-2" className="sb-btn-secondary text-[12px]"><ArrowLeft size={14} /> Back</button>
              <button onClick={() => setStep(3)} data-testid="verify-next-2" className="sb-btn-primary flex-1">Next <ArrowRight size={16} /></button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div data-testid="verify-step-3" className="sb-card p-6 space-y-4">
            <div className="sb-eyebrow">Confirm Interest</div>
            <p className="text-[13.5px] text-[#A1A1AA] leading-relaxed">
              SpotBounty connects businesses with independent vending operators.
              By confirming below you agree to be introduced to a local independent operator.
            </p>
            <label className="sb-card p-4 flex items-start gap-3 text-[13px]">
              <input type="checkbox" data-testid="verify-consent" className="mt-1 accent-[#FFC107]" checked={form.consent} onChange={set("consent")} />
              <span>
                I confirm that I am authorized to discuss vending service for this location and agree to be introduced to an independent vending operator through SpotBounty.
              </span>
            </label>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(2)} data-testid="verify-back-3" className="sb-btn-secondary text-[12px]"><ArrowLeft size={14} /> Back</button>
              <button
                disabled={!canSubmit}
                onClick={() => { setSubmitted(true); toast.success("Interest confirmed."); }}
                data-testid="verify-submit"
                className="sb-btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Confirm Interest
              </button>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 text-[12px] text-[#A1A1AA]">
          <ShieldCheck size={14} className="text-[#FFC107] mt-0.5" />
          Your information is only shared with the specific operator introduced to you.
        </div>
      </div>
    </div>
  );
}
